import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import SubcategoryBarChart from './SubcategoryBarChart'; 
import DayCategoryDetailModal from './SubcategoryDetailModal'; // Import the new modal

const itemCategories = {
  // Auditiv Items
  'ba_aud_01': 'Auditiv',
  'ba_aud_0111': 'Auditiv',


  // Visuell Items
  'ba_vis_01': 'Visuell',
  'ba_vis_0111': 'Visuell',


   // Taktil Items
  'ba_tak_01': 'Taktil',
  'ba_tak_0111': 'Taktil',
  
  
  // Geruch/Geschmack Items,
  'ba_ge_01': 'Geruch_Geschmack',
  'ba_ge_0111': 'Geruch_Geschmack',
  
  
  // Unpredictability Items
  'ba_unp_01': 'Unvorhersehbarkeit',
  'ba_unp_0111': 'Unvorhersehbarkeit',

  
  // Sonstiges Items
  'ba_so_01': 'Sonstiges',
  'ba_so_0111': 'Sonstiges',
  
};

const categoryColors = {
  Auditiv: '#F29400',
  Visuell: '#AF122C',
  Taktil: '#EB6A27',
  Geruch_Geschmack: '#005B9A',
  Unvorhersehbarkeit: '#37A42C',
  Sonstiges: '#646567'
};


const getGermanWeekday = (dateStr) => {
  const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  
  // Parsing date here
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return {
      abbreviation: '??',
      isWeekend: false
    };
  }
  
  return {
    abbreviation: weekdays[date.getDay()],
    isWeekend: date.getDay() === 0 || date.getDay() === 6 // 0=Sunday, 6=Saturday
  };
};


const calculateDayNumber = (responses) => {
  const dateMap = {};
  const allDates = [];
  
  // Collect all dates from responses
  responses.forEach(response => {
    const parts = response.session_id.split('_');
    if (parts.length >= 3) {
      const dateStr = parts[1];
      // Validate date format
      if (dateStr && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        allDates.push(dateStr); 
      }
    }
  });

  if (allDates.length === 0) return { dayMap: {}, dateMap: {}, allWeekdays: [] };


  const sortedDates = [...new Set(allDates)].sort();
  
  // Parsing dates
  const parsedDates = sortedDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date
  }).filter(date => !isNaN(date.getTime())); // Filter out invalid dates

  if (parsedDates.length === 0) return { dayMap: {}, dateMap: {}, allWeekdays: [] };

  const startDate = new Date(Math.min(...parsedDates.map(d => d.getTime())));
  const endDate = new Date(Math.max(...parsedDates.map(d => d.getTime())));
  
  // Generate all weekdays in range
  const allWeekdays = [];
  let currentDate = new Date(startDate);
  let dayCounter = 0;
  

  while (currentDate <= endDate) {
    //const dateStr = currentDate.toISOString().split('T')[0];
    const dateStr = currentDate.getFullYear() + '-' +
      String(currentDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(currentDate.getDate()).padStart(2, '0');
    const { abbreviation, isWeekend } = getGermanWeekday(dateStr);
    
    if (!isWeekend) {
      dayCounter++;
      dateMap[dateStr] = {
        dayNumber: dayCounter,
        label: `${dayCounter} (${abbreviation})`,
        date: dateStr
      };
      allWeekdays.push(dateStr);
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Create session to day mapping
  const dayMap = {};
  responses.forEach(response => {
    const parts = response.session_id.split('_');
    if (parts.length >= 3) {
      const dateStr = parts[1];
      if (dateMap[dateStr]) {
        dayMap[response.session_id] = dateMap[dateStr];
      }
    }
  });

  return {
    dayMap,
    dateMap,
    allWeekdays,
    totalDays: dayCounter
  };
};

const ResponseBarChart = ({ responses }) => {

  // Add state for the modal
  const [selectedDay, setSelectedDay] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle bar click
  const handleBarClick = (data, index, event) => {
    setSelectedDay(data);
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDay(null);
  };
  //.......................................

  const [activeCategory, setActiveCategory] = useState('Auditiv');

  const processData = () => {
    const dayCounts = {};
    const daysWithAnyData = new Set();
    const daysWithCategoryItems = {}; // Track days that have items for each category

    const { dayMap, dateMap, allWeekdays } = calculateDayNumber(responses);

    // Initialize category tracking
    Object.keys(categoryColors).forEach(category => {
      daysWithCategoryItems[category] = new Set();
    });

    // First pass: Mark days that have any response data AND days with specific category items
    responses.forEach(response => {
      if (dayMap[response.session_id]) {
        const dayNumber = dayMap[response.session_id].dayNumber;
        daysWithAnyData.add(dayNumber);
        
        // Check if this item belongs to any of our categories
        Object.keys(categoryColors).forEach(category => {
          const categoryPrefix = category.toLowerCase().includes('geruch') ? 'ba_ge' : `ba_${category.toLowerCase().slice(0, 3)}`;
          
          if (response.item_name.startsWith(categoryPrefix)) {
            daysWithCategoryItems[category].add(dayNumber);
          }
        });
      }
    });

    // Initialize ALL weekdays in range
    allWeekdays.forEach(dateStr => {
      const dayInfo = dateMap[dateStr];
      const hasAnyData = daysWithAnyData.has(dayInfo.dayNumber);
      
      const dayData = {
        name: dayInfo.label,
        date: dayInfo.date,
        time: null, 
        hasAnyData: hasAnyData,
      };

      
      
      // Initialize all categories to 0 and add category presence flags
      Object.keys(categoryColors).forEach(category => {
        dayData[category] = 0;
        dayData[`has${category}Items`] = daysWithCategoryItems[category].has(dayInfo.dayNumber);
      });
      
      dayCounts[`Day_${dayInfo.dayNumber}`] = dayData;
    });

    // Second pass: Count actual valid responses
    responses.forEach(response => {
      // Skip system/control items
      if (['Start', 'decl_01', 'feedback', 'End', 
          'decl_auditiv', 'ba_aud_01_11', 'ba_aud_01_12', 'ba_aud_01_21', 'ba_aud_01_22', 'ba_aud_01_31', 'ba_aud_01_32',
          'decl_vis', 'ba_vis_01_11', 'ba_vis_01_12', 'ba_vis_01_21', 'ba_aud_01_22', 'ba_vis_01_31', 'ba_vis_01_32', 'ba_vis_01_41', 'ba_vis_01_42', 'ba_vis_01_51', 'ba_vis_01_52', 'ba_vis_01_61', 'ba_vis_01_62',
          'aff_1_pa', 'aff_2_na', 'aff_7_pa', 'aff_8_na', 'aff_11_na', 'aff_12_pa'].includes(response.item_name) || 
          !dayMap[response.session_id]) {
        return;
      }
    
      // Define valid category prefixes
      const validPrefixes = ['ba_aud', 'ba_vis', 'ba_tak', 'ba_ge', 'ba_unp', 'ba_so'];

    // Check if item starts with a valid prefix and matches the pattern _\d{2}_\d
    const hasValidPrefix = validPrefixes.some(prefix => response.item_name.startsWith(prefix));
    const hasValidPattern = /_\d{2}_\d$/.test(response.item_name);
    const isTextEntry = response.item_name.startsWith('textentry_');
    
    if ((!hasValidPrefix || !hasValidPattern) && !isTextEntry) {
      return;
    }

    // Handle both response formats:
    let antwort1, antwort2;
    if (response.item_resp.includes(';')) {
      [antwort2, antwort1] = response.item_resp.split(';');
    } else {
      antwort1 = response.item_resp;
    }

    // Different validation for textentry vs ba_ items
    let hasValidResponse = false;

    if (isTextEntry) {
    // For textentry items: count if Antwort-1 has text content
    hasValidResponse = (antwort1?.trim() !== '' && antwort1?.trim() !== '0');
    } else {
      // For ba_ items: only count if Antwort-1 is "1"
      hasValidResponse = (antwort1?.trim() === '1' || antwort2?.trim() === '1');
    }
    
    if (!hasValidResponse) return;

    if (!dayCounts[`Day_${dayMap[response.session_id].dayNumber}`].time) {
      const timeStr = response.session_id.split('_')[2];  // "10-32-03"
      const [hour, minute] = timeStr.split('-');          // extract HH and MM
      dayCounts[`Day_${dayMap[response.session_id].dayNumber}`].time = `${hour}:${minute}`;
    }

      // Find matching category
      let category;
      
      // First try exact match
      if (itemCategories[response.item_name]) {
        category = itemCategories[response.item_name];
      } 

      // Then try matching textentry items to their categories
      else if (response.item_name.startsWith('textentry_')) {
        const baseCategory = response.item_name.split('_')[1];
        category = Object.values(itemCategories).find(cat => 
          cat.toLowerCase().includes(baseCategory)
        );
      }
      // Then try BA items
      else if (response.item_name.startsWith('ba_')) {
        const prefix = response.item_name.split('_').slice(0, 3).join('_');
        category = itemCategories[prefix];
      }

      if (category) {
        const dayNumber = dayMap[response.session_id].dayNumber;
        const dayKey = `Day_${dayNumber}`;
        dayCounts[dayKey][category] += 1;
      }
  });

    return Object.values(dayCounts).sort((a, b) => 
      parseInt(a.name.split(' ')[0]) - parseInt(b.name.split(' ')[0])
    );
  };

  // Updated Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const hasAnyData = data.hasAnyData;
    const date = data.date;
    
    // Get the active category from the first payload item
    const activeCategory = payload[0].dataKey;
    
    // Check if this day has items for the active category (regardless of response value)
    const hasCategoryItems = data[`has${activeCategory}Items`];
    
    // Check if the active category has data (value > 0)
    const hasMatchingData = data[activeCategory] > 0;
    
    let statusInfo = '';
    if (!hasAnyData) {
      statusInfo = 'Keine Daten';
    } else if (!hasCategoryItems) {
      statusInfo = 'Daten vorhanden, aber keine ‚'+activeCategory+'‘-Barrieren';
    } else if (!hasMatchingData) {
      statusInfo = 'Daten vorhanden, aber keine ‚'+activeCategory+'‘-Barrieren';
      
    }
    
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '10px', 
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}>
        <p style={{ fontWeight: 'bold', margin: 0 }}>{`${label} (${date})`}</p>
        <p style={{ margin: '3px 0', fontStyle: 'italic', color: '#444' }}>
          Uhrzeit: {data.time}
        </p>
        {statusInfo && <p style={{ color: '#666', margin: '5px 0 0 0', fontStyle: 'italic' }}>{statusInfo}</p>}
        {hasMatchingData && payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color, margin: '3px 0' }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

  const chartData = processData();

  return (
    <div>
      <h4>Barrieren</h4>
      <p>
      Das Diagramm zeigt, in welchen Bereichen das Kind an den einzelnen Schultagen 
      Barrieren wahrgenommen hat. Die Angaben werden sechs Bereichen zugeordnet, 
      die sich neben dem Diagramm befinden: Auditiv, Visuell, Geruch und Geschmack, Taktil, 
      Unvorhersehbarkeit von Ereignissen sowie Sonstiges. Auf der X-Achse ist der Zeitraum 
      dargestellt, in dem der Fragebogen beantwortet wurde (Wochenenden ausgenommen). 
      Die Y-Achse zeigt die Häufigkeit der wahrgenommenen Barrieren, da an einem Tag 
      mehrere Barrieren aus unterschiedlichen Bereichen auftreten können. Diese Bereiche 
      umfassen weitere spezifische Unterkategorien (z. B. Lautstärke, helles Licht etc.) 
      und können unterhalb des Diagramms näher betrachtet werden. Darüber hinaus kann das Kind 
      eigene Barrieren angeben, nach denen nicht gezielt gefragt wurde. Diese Angaben befinden 
      sich in der Unterkategorie „Texteingabe“. Wenn Sie den Mauszeiger über einen Balken bewegen, 
      wird die jeweilige Häufigkeit angezeigt. Um die vom Kind eingegebene Antwort einzusehen, 
      klicken Sie auf den Balken der entsprechenden Kategorie. In dem neuen Fenster können Sie 
      anschließend den Mauszeiger über die Unterkategorie Texteingabe bewegen, um die konkrete 
      Antwort des Kindes anzuzeigen.
      </p>
      <div style={{ width: '100%', height: 500 }}>
        <h4>Häufigkeit der Barrieren im Verlauf</h4>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          barSize={Math.max(20, 100 / chartData.length)} // Dynamic width
          barGap={1} // Small gap between bars
          barCategoryGap={Math.max(5, 30 / chartData.length)}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45} 
              textAnchor="end" 
              height={70} 
            />
            <YAxis 
              label={{ value: 'Häufigkeit', angle: -90, position: 'insideLeft' }} 
              domain={[0, 'dataMax']}
              //tickCount={Math.max(2, activeCategoriesCount + 1)}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              layout="vertical" 
              verticalAlign="middle" 
              align="right"
              wrapperStyle={{ paddingLeft: 20 }}
              onClick={(e) => {
                const category = e.value.replace('/', '_');
                setActiveCategory(category); // Set the clicked category as active
              }}
            />
            
            {/* Show all bars but hide all except the active one */}
            {Object.entries(categoryColors).map(([category, color]) => (
              <Bar 
                key={category} 
                dataKey={category} 
                stackId="a" 
                fill={color}
                name={category.replace('_', '/')}
                hide={category !== activeCategory} // Only show active category
                onClick={handleBarClick} // Add click handler
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <DayCategoryDetailModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        dayData={selectedDay}
        category={activeCategory}
        responses={responses}
      />
      {/* Add the subcategory chart component */}
      <SubcategoryBarChart responses={responses} /> 
    </div>
  );
};

export default ResponseBarChart;